import { Box, Flex, styled } from '@withjoy/joykit';
import { defaultTheme, pxToRem } from '@withjoy/joykit/theme';
import { rgba } from '@shared/utils/style/rgba';
import { animationTransition } from '@shared/utils/animationTransition';

/**
 * Outer container with entrance-animation keyframes (BubbleOpacity,
 * BubbleTransform, Bounce). Matches `StyledMapPinLabelContainer` from
 * MapPin.styles.tsx.
 */
export const StyledPinContainer = styled(Box)<{
  $isAnimate?: boolean;
  $isHoverBounce?: boolean;
  $isActive?: boolean;
  $isClickable?: boolean;
  $isCardVisible?: boolean;
  $showOnTop?: boolean;
}>`
  @keyframes BubbleOpacity {
    0% {
      opacity: 0;
    }
    70% {
      opacity: 1;
    }
    100% {
      opacity: 1;
    }
  }

  @keyframes BubbleTransform {
    0% {
      transform: scale(0);
    }
    70% {
      transform: scale(1.3);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes Bounce {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-8px);
    }
  }

  cursor: default;
  transition: ${animationTransition('transform')};
  z-index: ${props => (props.$showOnTop ? 'var(--joykit-z-index-popover, 1000)' : 'auto')};

  ${props =>
    (props.$isActive || props.$isCardVisible) &&
    `
    transform: scale(1.1);
    .map-pin-image { display: block; }
  `}

  &:hover {
    ${props =>
      (props.$isClickable || props.$isActive) &&
      `
      cursor: pointer;
      transform: scale(1.1);
      z-index: var(--joykit-z-index-popover, 1000);
      .map-pin-image { display: block; }
    `}
  }

  ${props =>
    props.$isAnimate &&
    `
    opacity: 0;
    transform: scale(0);
    animation: BubbleOpacity 0.7s ease-in-out forwards, BubbleTransform 0.7s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
  `}

  ${props =>
    props.$isHoverBounce &&
    `
    z-index: var(--joykit-z-index-popover, 1000) !important;

    @media (min-width: ${defaultTheme.breakpoints.sm}) {
      animation: Bounce 0.7s ease-in-out infinite;
      will-change: transform;
    }

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      transform: translateY(-4px);
    }
  `}
`;
StyledPinContainer.displayName = 'StyledPinContainer';

/**
 * The `pill` variant is new and used only by the places map, so its shadow is
 * built from the `black` theme token (#333333) via the shared `rgba` helper.
 *
 * `compact` and `label` keep their original hand-written value byte for byte:
 * they are on live guest and admin maps, and this change is not meant to alter
 * any pin that already ships.
 */
const PILL_SHADOW = `0 2px 8px ${rgba(defaultTheme.colors.black, 0.18)}`;
const COMPACT_SHADOW = '0 1px 5px -1px rgba(0, 0, 0, 0.5)';

/** Pill-shaped bubble (the main label element). */
export const StyledPinBubble = styled(Flex)<{
  $isHighlighted?: boolean;
  $isClickable?: boolean;
  $isActive?: boolean;
  $isCardVisible?: boolean;
  $mapPinColor?: string;
  $mapPinHighlightColor?: string;
  $hoverColor?: string;
  $variant?: 'compact' | 'label' | 'pill';
}>`
  align-items: center;
  height: ${props => (props.$variant === 'pill' ? pxToRem(40) : pxToRem(32))};
  border-radius: ${props => (props.$variant === 'pill' ? pxToRem(20) : pxToRem(16))};
  box-shadow: ${props => (props.$variant === 'pill' ? PILL_SHADOW : COMPACT_SHADOW)};
  white-space: nowrap;
  column-gap: ${pxToRem(8)};
  transition: ${animationTransition('background-color', 'color')};

  padding: ${props => (props.$variant === 'label' ? `0 ${pxToRem(16)}` : props.$variant === 'pill' ? `0 ${pxToRem(14)}` : pxToRem(8))};
  ${props => props.$variant === 'pill' && `font-weight: 600;`}
  ${props => props.$variant === 'compact' && `min-width: ${pxToRem(64)}; justify-content: center;`}

  color: ${props => (props.$isHighlighted ? 'white' : defaultTheme.colors.typePrimaryDark)};
  background-color: ${props =>
    props.$isHighlighted
      ? (defaultTheme.colors[props.$mapPinHighlightColor as keyof typeof defaultTheme.colors] ?? props.$mapPinHighlightColor ?? defaultTheme.colors.mono14)
      : (defaultTheme.colors[props.$mapPinColor as keyof typeof defaultTheme.colors] ?? props.$mapPinColor ?? 'white')};
`;
StyledPinBubble.displayName = 'StyledPinBubble';

/** Downward triangle tip. */
export const StyledPinTip = styled(Box)<{
  $isHighlighted?: boolean;
  $mapPinColor?: string;
  $mapPinHighlightColor?: string;
  $hoverColor?: string;
  $isClickable?: boolean;
  $isActive?: boolean;
  $isCardVisible?: boolean;
}>`
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 10px solid
    ${props =>
      props.$isHighlighted
        ? (defaultTheme.colors[props.$mapPinHighlightColor as keyof typeof defaultTheme.colors] ?? props.$mapPinHighlightColor ?? defaultTheme.colors.mono14)
        : (defaultTheme.colors[props.$mapPinColor as keyof typeof defaultTheme.colors] ?? props.$mapPinColor ?? 'white')};
  margin-top: -1px;
  transition: ${animationTransition('border-color')};
`;
StyledPinTip.displayName = 'StyledPinTip';

/** Shadow layer behind the triangle tip. */
export const StyledPinTipShadow = styled(Box)``;
StyledPinTipShadow.displayName = 'StyledPinTipShadow';

/** Photo thumbnail next to the bubble. */
export const StyledPinImage = styled(Box)``;
StyledPinImage.displayName = 'StyledPinImage';
